/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRight,
    Award,
    HeartPulse,
    Building2,
    Rocket,
    Users,
    CheckCircle2,
    Quote,
} from "lucide-react";
import { ASSETS, SITE, TRUST_POINTS, CREDENTIALS, PILLARS } from "@/constants/site";
import api from "@/lib/api";

export default function Home() {
    const [services, setServices] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const [impact, setImpact] = useState([]);

    useEffect(() => {
        api.get("/services").then(({ data }) => setServices(data.slice(0, 6)));
        api.get("/testimonials").then(({ data }) => setTestimonials(data));
        api.get("/impact").then(({ data }) => setImpact(data));
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative hero-gradient text-white overflow-hidden min-h-[88vh] flex items-center">
                <div
                    className="absolute inset-0 opacity-[0.08] bg-grain pointer-events-none"
                    aria-hidden
                />
                <img
                    src={ASSETS.knoxvilleSkyline}
                    alt=""
                    className="absolute right-0 top-1/4 w-[55%] opacity-15 pointer-events-none mix-blend-soft-light"
                    aria-hidden
                />

                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-32 grid lg:grid-cols-12 gap-12 items-center w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="lg:col-span-7"
                    >
                        <div className="inline-flex items-center bg-white p-3 mb-8 rounded-sm shadow-2xl">
                            <img
                                src={ASSETS.logo}
                                alt="Hopewell Consulting LLC"
                                data-testid="hero-logo"
                                className="h-20 w-auto"
                            />
                        </div>
                        <div className="flex items-center gap-3 mb-8">
                            <span className="gold-rule" />
                            <span className="overline">
                                Knoxville, TN · CT · GA · NC · TX · Est. {new Date().getFullYear() - 4}
                            </span>
                        </div>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02]">
                            Turning Vision
                            <br />
                            <span className="text-[#D4AF37] italic">Into Reality.</span>
                        </h1>
                        <p className="mt-8 text-lg text-slate-300 max-w-xl leading-relaxed">
                            Business, nonprofit, real estate, and community
                            development consulting for leaders, organizations,
                            and entrepreneurs ready to build with structure,
                            strategy, and results.
                        </p>

                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link
                                to="/contact"
                                data-testid="hero-cta-schedule"
                                className="group inline-flex items-center gap-3 bg-[#D4AF37] text-[#0A192F] hover:bg-[#C5A059] rounded-sm px-8 py-4 font-bold text-sm uppercase tracking-widest transition-colors"
                            >
                                Schedule a Consultation
                                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                            </Link>
                            <Link
                                to="/services"
                                data-testid="hero-cta-services"
                                className="inline-flex items-center gap-3 bg-transparent border border-white/40 text-white hover:bg-white/10 rounded-sm px-8 py-4 font-semibold text-sm uppercase tracking-widest transition-colors"
                            >
                                View Services
                            </Link>
                        </div>

                        <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
                            {TRUST_POINTS.map((t) => (
                                <div
                                    key={t}
                                    className="flex items-center gap-2 text-xs text-slate-400 tracking-wide"
                                >
                                    <CheckCircle2 className="w-3.5 h-3.5 text-[#D4AF37]" />
                                    {t}
                                </div>
                            ))}
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.97 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="lg:col-span-5 relative"
                    >
                        <div className="relative max-w-md mx-auto">
                            <div className="relative aspect-[4/5]">
                                <div className="absolute -inset-3 border border-[#D4AF37]/60 rounded-sm" />
                                <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A] to-[#0A192F] overflow-hidden rounded-sm">
                                    <img
                                        src={ASSETS.reicoHeadshot}
                                        alt="Reico S. Hopewell, CEO of Hopewell Consulting"
                                        className="w-full h-full object-cover object-top"
                                    />
                                </div>
                            </div>
                            <div className="relative mt-6 sm:mt-0 sm:absolute sm:-bottom-6 sm:-left-6 bg-white text-[#0A192F] p-5 max-w-[240px] rounded-sm shadow-2xl border-l-4 border-[#D4AF37]">
                                <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-bold">
                                    Led by
                                </p>
                                <p className="font-serif text-xl leading-tight font-bold mt-1">
                                    Reico S. Hopewell
                                </p>
                                <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-1">
                                    CEO · Knoxville, TN
                                </p>
                                <div className="flex flex-wrap gap-1.5 mt-3">
                                    {CREDENTIALS.map((c) => (
                                        <span
                                            key={c.label}
                                            title={c.title}
                                            className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 bg-[#0A192F] text-[#D4AF37]"
                                        >
                                            {c.label}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* TRUST STRIP */}
            <section className="border-y border-slate-200 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
                    {[
                        { icon: Rocket, label: "Business Formation", slug: "business-startup" },
                        { icon: Building2, label: "Nonprofit Development", slug: "nonprofit-planning" },
                        { icon: HeartPulse, label: "Behavioral Health", slug: "behavioral-health-strategy" },
                        { icon: Users, label: "Community Impact", slug: "organizational-development" },
                    ].map(({ icon: Icon, label, slug }) => (
                        <Link
                            key={label}
                            to={`/services/${slug}`}
                            data-testid={`trust-strip-${slug}`}
                            className="flex items-center gap-3 text-[#0A192F] hover:text-[#D4AF37] transition-colors group"
                        >
                            <Icon className="w-5 h-5 text-[#D4AF37]" />
                            <span className="text-xs uppercase tracking-[0.18em] font-semibold border-b-2 border-transparent group-hover:border-[#D4AF37] transition-colors">
                                {label}
                            </span>
                        </Link>
                    ))}
                </div>
            </section>

            {/* INTRO */}
            <section className="py-28">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-16 items-start">
                    <div className="lg:col-span-5">
                        <span className="overline">01 · Who we are</span>
                        <h2 className="font-serif text-4xl sm:text-5xl font-bold mt-4 text-[#0A192F] leading-tight">
                            Executive-grade consulting,
                            <span className="italic"> rooted in Knoxville.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-6 text-slate-700 leading-relaxed text-lg">
                        <p>
                            Hopewell Consulting LLC works alongside founders,
                            nonprofit leaders, developers, and community
                            organizations to turn ideas into structured,
                            fundable, professional projects.
                        </p>
                        <p>
                            Led by Reico S. Hopewell — entrepreneur, CEO,
                            recovery leader, grant writer, and community
                            advocate — our practice combines lived experience
                            with professional expertise across business
                            formation, nonprofit development, real estate, and
                            behavioral health.
                        </p>
                        <Link
                            to="/about"
                            data-testid="home-about-link"
                            className="inline-flex items-center gap-2 text-[#0A192F] font-semibold link-underline pb-1"
                        >
                            Meet Reico Hopewell <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* PILLARS — Strategy · Structure · Results */}
            <section className="border-y border-slate-200 bg-white">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
                    <div className="grid md:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                        {PILLARS.map((p, idx) => (
                            <Link
                                key={p.label}
                                to="/services"
                                data-testid={`pillar-${p.label.toLowerCase()}`}
                                className="bg-white hover:bg-slate-50 p-10 group transition-colors block"
                            >
                                <span className="num-tag">{String(idx + 1).padStart(2, "0")}</span>
                                <h3 className="font-serif text-3xl font-bold mt-4 text-[#0A192F] group-hover:text-[#1E3A8A]">
                                    {p.label}
                                </h3>
                                <p className="mt-3 text-slate-600 leading-relaxed">
                                    {p.text}
                                </p>
                                <span className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#0A192F] group-hover:text-[#D4AF37] transition-colors">
                                    Explore Services <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                                </span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* SERVICES PREVIEW */}
            <section className="bg-[#0A192F] text-white py-28 relative overflow-hidden">
                <div className="absolute inset-0 bg-grain opacity-10" aria-hidden />
                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
                        <div>
                            <span className="overline">02 · Services</span>
                            <h2 className="font-serif text-4xl sm:text-5xl font-bold mt-4 leading-tight">
                                Built for leaders ready to <br />
                                <span className="text-[#D4AF37] italic">move</span>.
                            </h2>
                        </div>
                        <Link
                            to="/services"
                            data-testid="home-all-services-link"
                            className="text-sm font-semibold uppercase tracking-widest text-[#D4AF37] hover:text-white inline-flex items-center gap-2"
                        >
                            View all 12 services <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-700">
                        {services.map((s, idx) => (
                            <Link
                                key={s.id}
                                to={`/services/${s.slug}`}
                                data-testid={`home-service-${s.slug}`}
                                className="bg-[#0A192F] hover:bg-[#0F2143] transition-colors p-10 group block"
                            >
                                <div className="flex items-center justify-between mb-6">
                                    <span className="num-tag">
                                        0{idx + 1}
                                    </span>
                                    <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all" />
                                </div>
                                <h3 className="font-serif text-2xl font-bold leading-tight mb-3 text-white">
                                    {s.title}
                                </h3>
                                <p className="text-slate-400 text-sm leading-relaxed">
                                    {s.description}
                                </p>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* IMPACT */}
            <section className="py-28 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="text-center mb-16">
                        <span className="overline">03 · Impact</span>
                        <h2 className="font-serif text-4xl sm:text-5xl font-bold mt-4 text-[#0A192F]">
                            Numbers that built a community.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 sm:divide-x sm:divide-y-0 divide-y divide-slate-200 border border-slate-200 bg-white">
                        {impact.map((s) => (
                            <div
                                key={s.id}
                                className="p-8 lg:p-10 text-center"
                                data-testid={`home-impact-${s.id}`}
                            >
                                <div className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#D4AF37] leading-none break-words">
                                    {s.value}
                                </div>
                                <div className="mt-4 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#0A192F]">
                                    {s.label}
                                </div>
                                {s.description && (
                                    <div className="mt-2 text-xs text-slate-500">
                                        {s.description}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            {testimonials.length > 0 && (
                <section className="py-28">
                    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                        <div className="mb-16">
                            <span className="overline">04 · In Their Words</span>
                            <h2 className="font-serif text-4xl sm:text-5xl font-bold mt-4 text-[#0A192F] max-w-3xl leading-tight">
                                Partners who trusted the process.
                            </h2>
                        </div>
                        <div className="grid md:grid-cols-3 gap-8">
                            {testimonials.slice(0, 3).map((t) => (
                                <article
                                    key={t.id}
                                    className="exec-card p-8"
                                    data-testid={`testimonial-${t.id}`}
                                >
                                    <Quote className="w-8 h-8 text-[#D4AF37] mb-4" />
                                    <p className="text-slate-700 leading-relaxed">
                                        {t.content}
                                    </p>
                                    <div className="mt-6 pt-6 border-t border-slate-200">
                                        <div className="font-serif font-bold text-[#0A192F]">
                                            {t.author}
                                        </div>
                                        <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">
                                            {t.role}
                                            {t.organization && ` · ${t.organization}`}
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* CTA */}
            <section className="bg-[#0A192F] text-white py-24 border-t border-[#D4AF37]/30">
                <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
                    <Award className="w-10 h-10 text-[#D4AF37] mx-auto mb-6" />
                    <h2 className="font-serif text-4xl sm:text-5xl font-bold leading-tight">
                        Let's turn the vision into reality.
                    </h2>
                    <p className="mt-6 text-slate-300 max-w-2xl mx-auto">
                        Free 30-minute strategy call. We'll listen, ask the
                        sharp questions, and map your next three moves before
                        you leave.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-4 justify-center">
                        <Link
                            to="/contact"
                            data-testid="home-bottom-cta"
                            className="bg-[#D4AF37] text-[#0A192F] hover:bg-[#C5A059] rounded-sm px-8 py-4 font-bold text-sm uppercase tracking-widest"
                        >
                            Schedule a Consultation
                        </Link>
                        <a
                            href={`tel:${SITE.phoneLink}`}
                            data-testid="home-bottom-call"
                            className="border border-white/40 hover:bg-white/10 rounded-sm px-8 py-4 font-semibold text-sm uppercase tracking-widest"
                        >
                            Call {SITE.phone}
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
